import { useCallback, useEffect, useMemo, useState } from 'react';
import Select from 'react-select';
import {
  ArrowDown,
  ArrowDownToLine,
  ArrowUp,
  Binary,
  ChevronsUpDown,
  FileText,
  Pencil,
  Plus,
  RadioTower,
  Trash2,
  UploadCloud,
} from 'lucide-react';
import {
  deleteBinary,
  deleteDeviceOta,
  downloadBinary,
  getChangelog,
  listBinaries,
  listOtaDevices,
  renameBinary,
  saveChangelog,
  setDeviceOta,
  uploadBinary,
} from '../api/OtaApi';
import { getAllDevices } from '../api/api';
import { NO_COMPANY } from '../components/CompanyScopePicker';
import HubSelect from '../components/HubSelect';
import useMachinesOverview from '../hooks/useMachinesOverview';
import { ActionDrawer, ConfirmDialog, Toast } from '../components/ui';
import styles from './HubOtaManagement.module.scss';

const views = [
  { id: 'rollouts', label: 'Device rollouts', icon: RadioTower },
  { id: 'binaries', label: 'Binary library', icon: Binary },
  { id: 'notes', label: 'Release notes', icon: FileText },
];

// Rollout table columns. `sortValue` produces the value the header sort
// compares; strings are compared with a numeric-aware locale compare.
const ROLLOUT_COLUMNS = [
  { key: 'nickname', label: 'Machine', sortValue: row => row.nickname },
  { key: 'serial_number', label: 'Serial number', sortValue: row => row.serial_number },
  { key: 'companyName', label: 'Company', sortValue: row => row.companyName },
  { key: 'target_version', label: 'Target version', sortValue: row => row.target_version || '' },
  { key: 'current_version', label: 'Current version', sortValue: row => row.current_version || '' },
  { key: 'binaryFilename', label: 'Binary', sortValue: row => row.binaryFilename },
  { key: 'autorestart', label: 'Restart', sortValue: row => (row.autorestart ? 1 : 0) },
  { key: 'last_seen', label: 'Last seen', sortValue: row => row.last_seen || 0 },
];

const BINARY_COLUMNS = [
  { key: 'filename', label: 'Filename', sortValue: row => row.filename },
  { key: 'version_string', label: 'Version', sortValue: row => row.version_string || '' },
  { key: 'size', label: 'Size', sortValue: row => row.size || 0 },
  { key: 'modified', label: 'Modified', sortValue: row => row.modified || 0 },
  { key: 'referenceCount', label: 'Rollout policies', sortValue: row => row.referenceCount },
];

const nextSortConfig = (current, key) =>
  current.key === key
    ? { key, direction: current.direction === 'asc' ? 'desc' : 'asc' }
    : { key, direction: 'asc' };

const compareRows = (a, b, column, direction) => {
  const aValue = column.sortValue(a);
  const bValue = column.sortValue(b);
  const comparison =
    typeof aValue === 'number' && typeof bValue === 'number'
      ? aValue - bValue
      : String(aValue ?? '').localeCompare(String(bValue ?? ''), undefined, { numeric: true });
  return direction === 'asc' ? comparison : -comparison;
};

// Header cells shared by both tables: click to sort, click again to reverse.
const renderSortableHeaders = (columns, sortConfig, onSort) =>
  columns.map(column => {
    const active = sortConfig.key === column.key;
    return (
      <th
        key={column.key}
        aria-sort={active ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : 'none'}
      >
        <button
          type="button"
          className={`${styles.sortButton} ${active ? styles.sortActive : ''}`}
          onClick={() => onSort(column.key)}
        >
          <span>{column.label}</span>
          {active ? (
            sortConfig.direction === 'asc' ? (
              <ArrowUp size={12} />
            ) : (
              <ArrowDown size={12} />
            )
          ) : (
            <ChevronsUpDown size={12} className={styles.sortIdle} />
          )}
        </button>
      </th>
    );
  });

function HubOtaManagement() {
  const [activeView, setActiveView] = useState('rollouts');
  const [devices, setDevices] = useState([]);
  const [binaries, setBinaries] = useState([]);
  const [allDevices, setAllDevices] = useState([]);
  const [drawer, setDrawer] = useState(null);
  const [editingDevice, setEditingDevice] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [renameFilename, setRenameFilename] = useState('');
  const [newFilename, setNewFilename] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [uploadFilename, setUploadFilename] = useState('');
  const [uploadVersionString, setUploadVersionString] = useState('');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [newDeviceSerial, setNewDeviceSerial] = useState('');
  const [newDeviceVersion, setNewDeviceVersion] = useState('');
  const [newDeviceBinary, setNewDeviceBinary] = useState('');
  const [newDeviceAutorestart, setNewDeviceAutorestart] = useState(false);
  const [changelogContent, setChangelogContent] = useState('');
  const [savedChangelog, setSavedChangelog] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [changelogLoading, setChangelogLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [rolloutFilter, setRolloutFilter] = useState('');
  const [companyFilter, setCompanyFilter] = useState('');
  const [versionFilter, setVersionFilter] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'serial_number', direction: 'asc' });
  const [binarySortConfig, setBinarySortConfig] = useState({ key: 'modified', direction: 'desc' });
  const [referenceBinary, setReferenceBinary] = useState(null);

  const overview = useMachinesOverview();

  const fetchDevices = useCallback(async () => {
    const response = await listOtaDevices();
    setDevices(response.data || []);
  }, []);

  const fetchBinaries = useCallback(async () => {
    const response = await listBinaries();
    setBinaries(response.data || []);
  }, []);

  const fetchAllDevices = useCallback(async () => {
    try {
      const response = await getAllDevices();
      setAllDevices(response.data || []);
    } catch {
      setAllDevices([]);
    }
  }, []);

  const fetchChangelogData = useCallback(async () => {
    try {
      const response = await getChangelog();
      const content = response.data?.content || '';
      setChangelogContent(content);
      setSavedChangelog(content);
    } catch {
      setChangelogContent('');
      setSavedChangelog('');
    }
  }, []);

  useEffect(() => {
    Promise.all([fetchDevices(), fetchBinaries(), fetchAllDevices(), fetchChangelogData()]).catch(
      error =>
        setErrorMessage(
          `Could not load firmware workspace: ${error.response?.data?.detail || error.message}`
        )
    );
  }, [fetchAllDevices, fetchBinaries, fetchChangelogData, fetchDevices]);

  const clearMessages = () => {
    setSuccessMessage('');
    setErrorMessage('');
  };

  const closeDrawer = () => {
    if (loading || uploading) {
      return;
    }
    setDrawer(null);
    setEditingDevice(null);
    setNewDeviceSerial('');
    setNewDeviceVersion('');
    setNewDeviceBinary('');
    setNewDeviceAutorestart(false);
    setUploadFile(null);
    setUploadFilename('');
    setUploadVersionString('');
    setUploadProgress(0);
    setRenameFilename('');
    setNewFilename('');
  };

  const selectBinaryForNewDevice = filename => {
    const binary = binaries.find(item => item.filename === filename);
    setNewDeviceBinary(filename);
    if (binary) {
      setNewDeviceVersion(binary.version_string);
    }
  };

  const selectBinaryForEditingDevice = filename => {
    const binary = binaries.find(item => item.filename === filename);
    setEditingDevice(current => ({
      ...current,
      binary_filename: filename,
      target_version: binary?.version_string || current.target_version,
    }));
  };

  const openDeviceEditor = device => {
    setEditingDevice({
      serial_number: device.serial_number,
      nickname: device.nickname,
      target_version: device.target_version || '',
      binary_filename: device.binary_url ? device.binary_url.split('/').pop() : '',
      autorestart: Boolean(device.autorestart),
    });
    setDrawer('edit-device');
  };

  const handleAddDeviceOta = async () => {
    if (!newDeviceSerial.trim()) {
      setErrorMessage('Enter a device serial number.');
      return;
    }
    try {
      setLoading(true);
      clearMessages();
      await setDeviceOta(newDeviceSerial.trim(), {
        target_version: newDeviceVersion,
        binary_filename: newDeviceBinary,
        autorestart: newDeviceAutorestart,
      });
      setSuccessMessage(`Firmware rollout configured for ${newDeviceSerial.trim()}.`);
      setDrawer(null);
      setNewDeviceSerial('');
      setNewDeviceVersion('');
      setNewDeviceBinary('');
      setNewDeviceAutorestart(false);
      await fetchDevices();
    } catch (error) {
      setErrorMessage(`Could not add rollout: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateDeviceOta = async () => {
    if (!editingDevice) {
      return;
    }
    try {
      setLoading(true);
      clearMessages();
      await setDeviceOta(editingDevice.serial_number, {
        target_version: editingDevice.target_version,
        binary_filename: editingDevice.binary_filename,
        autorestart: editingDevice.autorestart,
      });
      setSuccessMessage(`Firmware rollout updated for ${editingDevice.serial_number}.`);
      setDrawer(null);
      setEditingDevice(null);
      await fetchDevices();
    } catch (error) {
      setErrorMessage(`Could not update rollout: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadBinary = async () => {
    if (!uploadFile || !uploadVersionString.trim()) {
      return;
    }
    try {
      setUploading(true);
      clearMessages();
      setUploadProgress(0);
      await uploadBinary(
        uploadFile,
        uploadFilename.trim(),
        uploadVersionString.trim(),
        progressEvent =>
          setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total))
      );
      setSuccessMessage(`${uploadFilename || uploadFile.name} added to the binary library.`);
      setDrawer(null);
      setUploadFile(null);
      setUploadFilename('');
      setUploadVersionString('');
      setUploadProgress(0);
      await Promise.all([fetchBinaries(), fetchChangelogData()]);
    } catch (error) {
      setErrorMessage(`Could not upload binary: ${error.response?.data?.detail || error.message}`);
      setUploadProgress(0);
    } finally {
      setUploading(false);
    }
  };

  const handleRenameBinary = async () => {
    try {
      setLoading(true);
      clearMessages();
      await renameBinary(renameFilename, newFilename.trim());
      setSuccessMessage(`Binary renamed to ${newFilename.trim()}.`);
      setDrawer(null);
      setRenameFilename('');
      setNewFilename('');
      await Promise.all([fetchBinaries(), fetchDevices()]);
    } catch (error) {
      setErrorMessage(`Could not rename binary: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadBinary = async filename => {
    try {
      setLoading(true);
      clearMessages();
      const response = await downloadBinary(filename);
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      setSuccessMessage(`Download for ${filename} started.`);
    } catch (error) {
      setErrorMessage(
        `Could not download binary: ${error.response?.data?.detail || error.message}`
      );
    } finally {
      setLoading(false);
    }
  };

  const confirmDelete = async () => {
    if (!pendingDelete) {
      return;
    }
    try {
      setLoading(true);
      clearMessages();
      if (pendingDelete.type === 'device') {
        await deleteDeviceOta(pendingDelete.serialNumber);
        await fetchDevices();
      } else {
        await deleteBinary(pendingDelete.filename);
        await fetchBinaries();
      }
      setSuccessMessage(`${pendingDelete.label} removed.`);
      setPendingDelete(null);
    } catch (error) {
      setErrorMessage(`Could not remove item: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveChangelog = async () => {
    try {
      setChangelogLoading(true);
      clearMessages();
      await saveChangelog(changelogContent);
      setSavedChangelog(changelogContent);
      setSuccessMessage('Release notes saved.');
    } catch (error) {
      setErrorMessage(
        `Could not save release notes: ${error.response?.data?.detail || error.message}`
      );
    } finally {
      setChangelogLoading(false);
    }
  };

  const formatFileSize = bytes => {
    if (!bytes) {
      return '0 B';
    }
    const units = ['B', 'KB', 'MB', 'GB'];
    const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    return `${(bytes / 1024 ** index).toFixed(index ? 1 : 0)} ${units[index]}`;
  };
  const formatDate = timestamp => (timestamp ? new Date(timestamp * 1000).toLocaleString() : '—');
  const formatLastSeen = timestamp =>
    timestamp ? new Date(timestamp * 1000).toLocaleString() : 'Never';

  const availableSerials = useMemo(
    () =>
      allDevices
        .map(device =>
          typeof device === 'string'
            ? device
            : device.serial_number || device.serial || device.device_id || ''
        )
        .filter(Boolean),
    [allDevices]
  );

  // Company names are not part of the OTA payload, so they are resolved from
  // the shared machines overview.
  const machineBySerial = useMemo(() => {
    const map = new Map();
    (overview?.machines ?? []).forEach(machine => map.set(machine.serial_number, machine));
    return map;
  }, [overview]);

  const rolloutRows = useMemo(
    () =>
      devices.map(device => {
        const isDefault = device.serial_number === 'default';
        const machine = machineBySerial.get(device.serial_number);
        return {
          ...device,
          isDefault,
          nickname: isDefault
            ? 'Default policy'
            : device.nickname || machine?.nickname || device.serial_number,
          companyName: isDefault ? '' : machine?.company_name || '',
          binaryFilename: device.binary_url?.split('/').pop() || '',
          current_version: isDefault ? '' : device.current_version || '',
        };
      }),
    [devices, machineBySerial]
  );

  // Only hubs that actually have an override are offered in the hub filter.
  const policySerials = useMemo(
    () => rolloutRows.filter(row => !row.isDefault).map(row => row.serial_number),
    [rolloutRows]
  );

  const companyOptions = useMemo(() => {
    const names = [...new Set(rolloutRows.map(row => row.companyName).filter(Boolean))];
    names.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    const options = names.map(name => ({ value: name, label: name }));
    if (rolloutRows.some(row => !row.companyName)) {
      options.unshift({ value: NO_COMPANY, label: 'No company' });
    }
    return options;
  }, [rolloutRows]);

  const versionOptions = useMemo(() => {
    const versions = [...new Set(rolloutRows.map(row => row.target_version).filter(Boolean))];
    versions.sort((a, b) => a.localeCompare(b, undefined, { numeric: true }));
    return versions.map(version => ({ value: version, label: version }));
  }, [rolloutRows]);

  const activeFilterCount = [rolloutFilter, companyFilter, versionFilter].filter(Boolean).length;

  const clearFilters = () => {
    setRolloutFilter('');
    setCompanyFilter('');
    setVersionFilter('');
  };

  const visibleRollouts = useMemo(() => {
    const filtered = rolloutRows.filter(row => {
      if (rolloutFilter && row.serial_number !== rolloutFilter) {
        return false;
      }
      if (companyFilter === NO_COMPANY) {
        if (row.companyName) {
          return false;
        }
      } else if (companyFilter && row.companyName !== companyFilter) {
        return false;
      }
      if (versionFilter && row.target_version !== versionFilter) {
        return false;
      }
      return true;
    });
    const column = ROLLOUT_COLUMNS.find(item => item.key === sortConfig.key) || ROLLOUT_COLUMNS[1];

    return [...filtered].sort((a, b) => {
      // The default policy is a fallback rather than a hub, so it stays on top.
      if (a.isDefault !== b.isDefault) {
        return a.isDefault ? -1 : 1;
      }
      return compareRows(a, b, column, sortConfig.direction);
    });
  }, [rolloutRows, rolloutFilter, companyFilter, versionFilter, sortConfig]);

  const handleSort = key => setSortConfig(current => nextSortConfig(current, key));
  const handleBinarySort = key => setBinarySortConfig(current => nextSortConfig(current, key));

  const renderRolloutCell = (row, key) => {
    switch (key) {
      case 'nickname':
        return row.isDefault ? (
          <span className={styles.defaultTag}>Default policy</span>
        ) : (
          <span className={styles.machineCell}>
            <strong>{row.nickname}</strong>
          </span>
        );
      case 'serial_number':
        return row.isDefault ? '—' : <span className={styles.monoCell}>{row.serial_number}</span>;
      case 'companyName':
        return row.isDefault ? '—' : row.companyName || '—';
      case 'target_version':
        return row.target_version || 'No target';
      case 'current_version':
        return row.isDefault ? '—' : row.current_version || 'Unknown';
      case 'binaryFilename':
        return (
          <span className={styles.binaryCell} title={row.binaryFilename}>
            {row.binaryFilename || 'None selected'}
          </span>
        );
      case 'autorestart':
        return row.autorestart ? 'Automatic' : 'Manual';
      case 'last_seen':
        return row.isDefault ? '—' : formatLastSeen(row.last_seen);
      default:
        return null;
    }
  };

  // Rollout policies grouped by the binary they target, so the library can show
  // both the count and the machines behind it.
  const referencesByBinary = useMemo(() => {
    const map = new Map();
    rolloutRows.forEach(row => {
      if (!row.binaryFilename) {
        return;
      }
      const existing = map.get(row.binaryFilename);
      if (existing) {
        existing.push(row);
      } else {
        map.set(row.binaryFilename, [row]);
      }
    });
    return map;
  }, [rolloutRows]);

  const getBinaryReferences = useCallback(
    filename => referencesByBinary.get(filename) || [],
    [referencesByBinary]
  );

  const sortedBinaries = useMemo(() => {
    const rows = binaries.map(binary => ({
      ...binary,
      referenceCount: (referencesByBinary.get(binary.filename) || []).length,
    }));
    const column =
      BINARY_COLUMNS.find(item => item.key === binarySortConfig.key) || BINARY_COLUMNS[0];
    return rows.sort((a, b) => compareRows(a, b, column, binarySortConfig.direction));
  }, [binaries, referencesByBinary, binarySortConfig]);

  const renderBinaryCell = (row, key) => {
    switch (key) {
      case 'filename':
        return (
          <span className={styles.binaryNameCell} title={row.filename}>
            <Binary size={14} />
            <strong>{row.filename}</strong>
          </span>
        );
      case 'version_string':
        return row.version_string || 'Not specified';
      case 'size':
        return formatFileSize(row.size);
      case 'modified':
        return formatDate(row.modified);
      case 'referenceCount':
        return row.referenceCount ? (
          <button
            type="button"
            className={styles.referenceButton}
            onClick={() => setReferenceBinary(row.filename)}
            title="Show the machines using this binary"
          >
            {row.referenceCount} {row.referenceCount === 1 ? 'policy' : 'policies'}
          </button>
        ) : (
          <span className={styles.referenceNone}>Not targeted</span>
        );
      default:
        return null;
    }
  };

  const viewCount = {
    rollouts: devices.length,
    binaries: binaries.length,
    notes: changelogContent ? 1 : 0,
  };

  return (
    <div className={styles.page}>
      <header className={styles.pageHeader}>
        <div>
          <span className={styles.eyebrow}>Hub deployment</span>
          <h1>Firmware rollout</h1>
          <p>Manage target firmware by device, keep binary artifacts, and publish release notes.</p>
        </div>
      </header>

      {successMessage && (
        <Toast tone="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Toast>
      )}
      {errorMessage && (
        <Toast tone="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Toast>
      )}

      <nav className={styles.viewSwitcher} aria-label="Firmware sections">
        {views.map(view => {
          const Icon = view.icon;
          return (
            <button
              key={view.id}
              type="button"
              className={activeView === view.id ? styles.viewActive : ''}
              onClick={() => setActiveView(view.id)}
            >
              <Icon size={17} />
              <span>{view.label}</span>
              <b>{viewCount[view.id]}</b>
            </button>
          );
        })}
      </nav>

      {activeView === 'rollouts' && (
        <section className={styles.panel}>
          <div className={styles.panelHeader}>
            <div>
              <h2>Device rollout policies</h2>
              <p>The default policy applies globally; device-specific entries override it.</p>
            </div>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={() => setDrawer('add-device')}
            >
              <Plus size={16} /> Add device policy
            </button>
          </div>

          {devices.length > 0 && (
            <div className={styles.filterBar}>
              <div className={styles.filterFields}>
                <div className={styles.filterField}>
                  <label htmlFor="rollout-hub-filter">Filter by hub</label>
                  <HubSelect
                    inputId="rollout-hub-filter"
                    value={rolloutFilter}
                    onChange={setRolloutFilter}
                    serials={policySerials}
                    placeholder="Search by nickname, serial or company…"
                    noOptionsMessage="No hub has a rollout override"
                  />
                </div>
                <div className={styles.filterField}>
                  <label htmlFor="rollout-company-filter">Filter by company</label>
                  <Select
                    inputId="rollout-company-filter"
                    classNamePrefix="hubSelect"
                    options={companyOptions}
                    value={companyOptions.find(option => option.value === companyFilter) || null}
                    onChange={option => setCompanyFilter(option ? option.value : '')}
                    isClearable
                    isSearchable
                    placeholder="All companies"
                    noOptionsMessage={() => 'No company on any policy'}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    menuPlacement="auto"
                    maxMenuHeight={360}
                  />
                </div>
                <div className={styles.filterField}>
                  <label htmlFor="rollout-version-filter">Filter by target version</label>
                  <Select
                    inputId="rollout-version-filter"
                    classNamePrefix="hubSelect"
                    options={versionOptions}
                    value={versionOptions.find(option => option.value === versionFilter) || null}
                    onChange={option => setVersionFilter(option ? option.value : '')}
                    isClearable
                    isSearchable
                    placeholder="All target versions"
                    noOptionsMessage={() => 'No target version set'}
                    menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                    menuPlacement="auto"
                    maxMenuHeight={360}
                  />
                </div>
              </div>
              <div className={styles.filterSummary}>
                <span>
                  Showing {visibleRollouts.length} of {devices.length}{' '}
                  {devices.length === 1 ? 'policy' : 'policies'}
                </span>
                {activeFilterCount > 0 && (
                  <button type="button" onClick={clearFilters}>
                    Clear {activeFilterCount === 1 ? 'filter' : 'filters'}
                  </button>
                )}
              </div>
            </div>
          )}

          {devices.length ? (
            <div className={styles.tableWrapper}>
              <table className={styles.rolloutTable}>
                <thead>
                  <tr>
                    {renderSortableHeaders(ROLLOUT_COLUMNS, sortConfig, handleSort)}
                    <th className={styles.actionsHeader}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleRollouts.map(row => {
                    return (
                      <tr
                        key={row.serial_number}
                        className={row.isDefault ? styles.defaultRow : ''}
                      >
                        {ROLLOUT_COLUMNS.map(column => (
                          <td key={column.key}>{renderRolloutCell(row, column.key)}</td>
                        ))}
                        <td className={styles.actionsCell}>
                          <div className={styles.rowActions}>
                            <button
                              type="button"
                              className={styles.iconButton}
                              onClick={() => openDeviceEditor(row)}
                              title="Edit rollout"
                            >
                              <Pencil size={15} />
                            </button>
                            {!row.isDefault && (
                              <button
                                type="button"
                                className={styles.iconDangerButton}
                                onClick={() =>
                                  setPendingDelete({
                                    type: 'device',
                                    serialNumber: row.serial_number,
                                    label: `Policy for ${row.serial_number}`,
                                  })
                                }
                                title="Remove override"
                              >
                                <Trash2 size={15} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                  {visibleRollouts.length === 0 && (
                    <tr>
                      <td className={styles.noMatchCell} colSpan={ROLLOUT_COLUMNS.length + 1}>
                        No rollout policy matches the current filters.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={styles.emptyState}>
              <RadioTower size={27} />
              <strong>No rollout policies</strong>
              <p>Add a default or device-specific firmware target.</p>
            </div>
          )}
        </section>
      )}

      {activeView === 'binaries' && (
        <section className={styles.panel}>
          <div className={styles.panelHeader}>
            <div>
              <h2>Firmware binary library</h2>
              <p>Device policies can only target binaries stored here.</p>
            </div>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={() => setDrawer('upload-binary')}
            >
              <UploadCloud size={16} /> Upload binary
            </button>
          </div>
          {binaries.length ? (
            <div className={styles.tableWrapper}>
              <table className={styles.binaryTable}>
                <thead>
                  <tr>
                    {renderSortableHeaders(BINARY_COLUMNS, binarySortConfig, handleBinarySort)}
                    <th className={styles.actionsHeader}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedBinaries.map(row => {
                    return (
                      <tr key={row.filename}>
                        {BINARY_COLUMNS.map(column => (
                          <td key={column.key}>{renderBinaryCell(row, column.key)}</td>
                        ))}
                        <td className={styles.actionsCell}>
                          <div className={styles.rowActions}>
                            <button
                              type="button"
                              className={styles.iconButton}
                              onClick={() => handleDownloadBinary(row.filename)}
                              title="Download"
                            >
                              <ArrowDownToLine size={15} />
                            </button>
                            <button
                              type="button"
                              className={styles.iconButton}
                              onClick={() => {
                                setRenameFilename(row.filename);
                                setNewFilename(row.filename);
                                setDrawer('rename-binary');
                              }}
                              title="Rename"
                            >
                              <Pencil size={15} />
                            </button>
                            <button
                              type="button"
                              className={styles.iconDangerButton}
                              onClick={() =>
                                setPendingDelete({
                                  type: 'binary',
                                  filename: row.filename,
                                  label: row.filename,
                                })
                              }
                              disabled={row.referenceCount > 0}
                              title={
                                row.referenceCount ? 'Remove rollout references first' : 'Delete'
                              }
                            >
                              <Trash2 size={15} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div className={styles.emptyState}>
              <Binary size={27} />
              <strong>No firmware binaries</strong>
              <p>Upload a binary before creating a rollout policy.</p>
            </div>
          )}
        </section>
      )}

      {activeView === 'notes' && (
        <section className={styles.panel}>
          <div className={styles.panelHeader}>
            <div>
              <h2>Release notes</h2>
              <p>Content served alongside firmware update information.</p>
            </div>
            <div className={styles.saveGroup}>
              {changelogContent !== savedChangelog && <span>Unsaved changes</span>}
              <button
                type="button"
                className={styles.primaryButton}
                onClick={handleSaveChangelog}
                disabled={changelogLoading || changelogContent === savedChangelog}
              >
                {changelogLoading ? 'Saving…' : 'Save notes'}
              </button>
            </div>
          </div>
          <div className={styles.editorWrap}>
            <textarea
              value={changelogContent}
              onChange={event => setChangelogContent(event.target.value)}
              placeholder="Describe the firmware changes, operator notes, and known issues…"
              disabled={changelogLoading}
            />
            <small>Plain text · changes are not published until saved</small>
          </div>
        </section>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        title={
          pendingDelete?.type === 'device'
            ? `Remove rollout override for ${pendingDelete.serialNumber}?`
            : `Delete ${pendingDelete?.label}?`
        }
        description={
          pendingDelete?.type === 'device'
            ? 'The hub will fall back to the default rollout policy.'
            : 'The firmware binary will be permanently deleted. This cannot be undone.'
        }
        confirmLabel={pendingDelete?.type === 'device' ? 'Remove' : 'Delete'}
        busyLabel="Removing…"
        busy={loading}
        onConfirm={confirmDelete}
        onCancel={() => setPendingDelete(null)}
      />

      <ActionDrawer
        open={drawer === 'add-device'}
        eyebrow="Device rollouts"
        title="Add device policy"
        description="Create a firmware override for one hub serial number."
        onClose={closeDrawer}
        closeDisabled={loading}
        footer={
          <>
            <button type="button" className={styles.secondaryButton} onClick={closeDrawer}>
              Cancel
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleAddDeviceOta}
              disabled={loading || !newDeviceSerial.trim()}
            >
              {loading ? 'Adding…' : 'Add policy'}
            </button>
          </>
        }
      >
        <div className={styles.drawerForm}>
          <div className={styles.selectField}>
            <label htmlFor="new-device-serial">Hub serial number</label>
            <HubSelect
              inputId="new-device-serial"
              value={newDeviceSerial}
              onChange={setNewDeviceSerial}
              extraSerials={availableSerials}
              allowCustom
              placeholder="Search or type a serial number…"
            />
            <small>
              Search by nickname, serial number or company, or type a serial number that is not
              registered yet.
            </small>
          </div>
          <label>
            <span>Firmware binary</span>
            <select
              value={newDeviceBinary}
              onChange={event => selectBinaryForNewDevice(event.target.value)}
            >
              <option value="">No binary selected</option>
              {binaries.map(binary => (
                <option key={binary.filename} value={binary.filename}>
                  {binary.filename} · {binary.version_string}
                </option>
              ))}
            </select>
          </label>
          <label>
            <span>Target version</span>
            <input
              value={newDeviceVersion}
              onChange={event => setNewDeviceVersion(event.target.value)}
              placeholder="Filled from the selected binary"
            />
          </label>
          <label className={styles.toggleField}>
            <input
              type="checkbox"
              checked={newDeviceAutorestart}
              onChange={event => setNewDeviceAutorestart(event.target.checked)}
            />
            <span>
              <strong>Restart automatically</strong>
              <small>Restart the hub after a successful update.</small>
            </span>
          </label>
        </div>
      </ActionDrawer>

      <ActionDrawer
        open={drawer === 'edit-device'}
        eyebrow="Device rollouts"
        title={
          editingDevice?.serial_number === 'default' ? 'Edit default policy' : 'Edit device policy'
        }
        description={editingDevice?.nickname || editingDevice?.serial_number || ''}
        onClose={closeDrawer}
        closeDisabled={loading}
        footer={
          <>
            <button type="button" className={styles.secondaryButton} onClick={closeDrawer}>
              Cancel
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleUpdateDeviceOta}
              disabled={loading}
            >
              {loading ? 'Applying…' : 'Apply policy'}
            </button>
          </>
        }
      >
        {editingDevice && (
          <div className={styles.drawerForm}>
            <label>
              <span>Firmware binary</span>
              <select
                value={editingDevice.binary_filename}
                onChange={event => selectBinaryForEditingDevice(event.target.value)}
              >
                <option value="">No binary selected</option>
                {binaries.map(binary => (
                  <option key={binary.filename} value={binary.filename}>
                    {binary.filename} · {binary.version_string}
                  </option>
                ))}
              </select>
            </label>
            <label>
              <span>Target version</span>
              <input
                value={editingDevice.target_version}
                onChange={event =>
                  setEditingDevice(current => ({ ...current, target_version: event.target.value }))
                }
              />
              <small>
                Normally derived from the selected binary; manual values remain supported.
              </small>
            </label>
            <label className={styles.toggleField}>
              <input
                type="checkbox"
                checked={editingDevice.autorestart}
                onChange={event =>
                  setEditingDevice(current => ({ ...current, autorestart: event.target.checked }))
                }
              />
              <span>
                <strong>Restart automatically</strong>
                <small>Restart the hub after a successful update.</small>
              </span>
            </label>
          </div>
        )}
      </ActionDrawer>

      <ActionDrawer
        open={drawer === 'upload-binary'}
        eyebrow="Binary library"
        title="Upload firmware"
        description="Store a firmware file and its version label for rollout policies."
        onClose={closeDrawer}
        closeDisabled={uploading}
        footer={
          <>
            <button
              type="button"
              className={styles.secondaryButton}
              onClick={closeDrawer}
              disabled={uploading}
            >
              Cancel
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleUploadBinary}
              disabled={uploading || !uploadFile || !uploadVersionString.trim()}
            >
              {uploading ? `Uploading ${uploadProgress}%` : 'Upload binary'}
            </button>
          </>
        }
      >
        <div className={styles.drawerForm}>
          <label className={styles.fileDrop}>
            <UploadCloud size={28} />
            <span>{uploadFile ? uploadFile.name : 'Choose a firmware binary'}</span>
            <small>
              {uploadFile ? formatFileSize(uploadFile.size) : 'Select a file from this device'}
            </small>
            <input
              type="file"
              onChange={event => {
                const file = event.target.files?.[0] || null;
                setUploadFile(file);
                if (file) {
                  setUploadFilename(file.name);
                }
              }}
            />
          </label>
          <label>
            <span>
              Stored filename <small>optional</small>
            </span>
            <input
              value={uploadFilename}
              onChange={event => setUploadFilename(event.target.value)}
              placeholder="Defaults to the uploaded filename"
            />
          </label>
          <label>
            <span>Version label</span>
            <input
              value={uploadVersionString}
              onChange={event => setUploadVersionString(event.target.value)}
              placeholder="e.g. 2.8.1"
            />
          </label>
          {uploading && (
            <progress className={styles.progress} max="100" value={uploadProgress}>
              {uploadProgress}%
            </progress>
          )}
        </div>
      </ActionDrawer>

      <ActionDrawer
        open={Boolean(referenceBinary)}
        eyebrow="Binary library"
        title="Machines on this binary"
        description={referenceBinary || ''}
        onClose={() => setReferenceBinary(null)}
        footer={
          <button
            type="button"
            className={styles.secondaryButton}
            onClick={() => setReferenceBinary(null)}
          >
            Close
          </button>
        }
      >
        <ul className={styles.referenceList}>
          {getBinaryReferences(referenceBinary).map(row => (
            <li key={row.serial_number}>
              <button
                type="button"
                onClick={() => {
                  setReferenceBinary(null);
                  setActiveView('rollouts');
                  clearFilters();
                  if (!row.isDefault) {
                    setRolloutFilter(row.serial_number);
                  }
                }}
                title="Show this policy in the rollout table"
              >
                <span className={styles.referenceIdentity}>
                  <strong>{row.nickname}</strong>
                  {!row.isDefault && <small>{row.serial_number}</small>}
                </span>
                <span className={styles.referenceMeta}>
                  <span>
                    {row.isDefault ? 'Fallback for all hubs' : row.companyName || 'No company'}
                  </span>
                  <span>Target {row.target_version || 'not set'}</span>
                </span>
              </button>
            </li>
          ))}
        </ul>
      </ActionDrawer>

      <ActionDrawer
        open={drawer === 'rename-binary'}
        eyebrow="Binary library"
        title="Rename binary"
        description={renameFilename}
        onClose={closeDrawer}
        closeDisabled={loading}
        footer={
          <>
            <button type="button" className={styles.secondaryButton} onClick={closeDrawer}>
              Cancel
            </button>
            <button
              type="button"
              className={styles.primaryButton}
              onClick={handleRenameBinary}
              disabled={loading || !newFilename.trim() || newFilename.trim() === renameFilename}
            >
              {loading ? 'Renaming…' : 'Rename binary'}
            </button>
          </>
        }
      >
        <div className={styles.drawerForm}>
          <label>
            <span>New filename</span>
            <input
              value={newFilename}
              onChange={event => setNewFilename(event.target.value)}
              autoFocus
            />
          </label>
        </div>
      </ActionDrawer>
    </div>
  );
}

export default HubOtaManagement;
